import React, { useState, useEffect, useContext } from "react";
import { AuthPermission } from "../../services/auth.service";
import ProtectedComponent from "../../components/ProtectedComponent/ProtectedComponent";
import {
  AddFee,
  BreadCrumbs,
  Button,
  Checkbox,
  ConfirmationDialog,
  Pagination,
  TableHeader,
  TableRow,
  Tabs,
  TextInput,
  UserSideBar,
} from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import OfficeSectionsHeader from "../../components/OfficeSectionsHeader/OfficeSectionsHeader";
import { formatNumToDec, getSorting } from "../../utils";
import {
  copyAllFeeToOffice,
  copyFeeToOffice,
  enableOfficeFee,
  resetFeeTableSort,
  searchOfficeFees,
  searchSystemFees,
} from "../../redux/slices/FeeSlice";
import EditFee from "../../components/forms/EditFee/EditFee";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import ViewFee from "../../components/ViewFee/ViewFee";
import { headersDocTemplates, headersInitialState } from "./FeesData";
import { CircularProgress } from "@mui/material";
import { sortObj } from "../../redux/slices/OfficeSlice";
import DataNotFound from "../../components/DataNotFound/DataNotFound";
import NotFound from "../../assets/images/no-fees.png";
import { useDebouncedCallback } from "use-debounce";
import { Fee } from "../../models/fee.model";
import { isEmpty } from "lodash";
import { ToastVariants } from "../../models";
import CheckIcon from "@mui/icons-material/Check";
import { ServiceOwner } from "../../models/service.model";
import { UserType } from "../../services/user.service";
import { restoreOfficeFee } from "../../redux/slices/FeeSlice";
import ArchivedFees from "./ArchivedFees";

const FeeToast = "Fee created at the Docnote level cannot be edited or deleted within an office.";
let previousCompletedItems: string[] = [];
interface CopiedFeeItem {
  feeId: string;
  completed: boolean;
}

const FeesScreen = ({ isAdminPage }: { isAdminPage: boolean }) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const { feeList, sort, loading } = useSelector((state: RootState) => state.fees);
  const currentOffice = useSelector((state: RootState) => state.office.currentOffice);
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const [showAddFeeSideBar, setShowAddFeeSideBar] = useState(false);
  const [editFeeFormVisible, setEditFeeFormVisible] = useState(false);
  const [viewFeeSidebarVisible, setViewFeeSidebarVisible] = useState(false);
  const [headers, setHeaders] = useState(headersInitialState);
  const [sortMethods, setSortMethods] = useState([true, false, false, false, false]);
  const [activeColumn, setActiveColumn] = useState<number>(-1);
  const [searchTerm, setSearchTerm] = useState("");
  const [officeId, setOfficeId] = useState(currentOffice?.officeId || "");
  const [firstRender, setFirstRender] = useState(true);
  const [tooltipStates, setTooltipStates] = useState<boolean[]>(new Array(feeList?.content ? feeList.content.length : 0).fill(false));
  const [selectedFee, setSelectedFee] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedFeeIdsForCopy, setSelectedFeeIdsForCopy] = useState<CopiedFeeItem[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [completedFees, setCompletedCopiedFees] = useState<string[]>([]);
  const [activeOwner, setActiveOwner] = useState(isAdminPage ? ServiceOwner.System : ServiceOwner.Office);

  const feeSideBar = (val: boolean) => setShowAddFeeSideBar(val);
  const sortKeys = headers.map(head => head.sortKey);
  const extraClasses = headers.map(head => head.extraClasses);
  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;
  const sortIcons = sortMethods.map((item, index) => (activeColumn === index ? (item ? headerIconAsc : headerIconDesc) : null));

  const onPageChange = async (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    await updateTable(activeOwner, { sortedBy: sort.sortedBy, pageNumber: newPage, rowsPerPage: sort.rowsPerPage }, searchTerm);
    setSelectedFeeIdsForCopy([]);
    setSelectAll(false);
  };

  const onRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    await updateTable(activeOwner, { sortedBy: sort.sortedBy, pageNumber: 0, rowsPerPage: parseInt(event.target.value, 10) }, searchTerm);
    setSelectedFeeIdsForCopy([]);
    setSelectAll(false);
  };

  const updateTable = async (owner: ServiceOwner, sortObj?: sortObj, searchText?: string) => {
    const pageNumToBeCalled = sortObj?.pageNumber ?? sort.pageNumber;
    const sizeToBeCalled = sortObj?.rowsPerPage ?? sort.rowsPerPage;
    const sortBy = sortObj?.sortedBy ?? sort.sortedBy;
    const searchT = searchText?.trim() ?? "";

    if (isAdminPage) {
      await dispatch(
        searchSystemFees({
          page: pageNumToBeCalled,
          size: sizeToBeCalled,
          sortType: sortBy,
          searchTerm: searchT.length > 2 ? searchT : "",
        })
      );
    } else {
      await dispatch(
        searchOfficeFees({
          officeId: officeId,
          page: pageNumToBeCalled,
          size: sizeToBeCalled,
          sortType: sortBy,
          searchTerm: searchT.length > 2 ? searchT : "",
          feeOwner: owner,
        })
      );
    }
  };

  const onHeaderClick = (index: number) => {
    if (sortKeys[index] === "") return;
    const sortMethodString = getSorting(sortKeys, index, sortMethods);
    setActiveColumn(index);
    setSortMethods([...sortMethods.slice(0, index), !sortMethods[index], ...sortMethods.slice(index + 1)]);
    updateTable(activeOwner, { sortedBy: sortMethodString, pageNumber: 0, rowsPerPage: sort.rowsPerPage }, searchTerm);
  };

  //const tooltipOptions = ["Edit Fee", "Enable Fee", "Delete Fee"];
  const tooltipOptions =
    currentUser?.permissions.includes(AuthPermission.Service) && activeIndex === 0
      ? isAdminPage
        ? ["Edit Fee"]
        : [
            "Edit Fee",
            ...(currentUser?.userType === UserType?.User ||
            currentUser?.userType === UserType?.OfficeAdmin ||
            currentUser?.userType === UserType?.SystemAdmin
              ? ["Copy Fee", "Delete Fee"]
              : ["Copy Fee"]),
          ]
      : // [ "Disable Service", "Delete Service"]
        ["View Fee"];
  const handleTooltipClick = (option: string, feeId: string) => {
    switch (option) {
      case "View Fee":
        setSelectedFee(feeId);
        setViewFeeSidebarVisible(true);
        break;
      case "Delete Fee":
        setSelectedFee(feeId);
        setConfirmationOpen(true);
        break;
      case tooltipOptions[0]:
        if (!feeList?.content.find(f => f.feeId === feeId)?.officeId && !isAdminPage) {
          toast?.openToast(FeeToast, 4000);
        } else {
          setSelectedFee(feeId);
          setEditFeeFormVisible(true);
        }
        break;
      case tooltipOptions[2]:
        if (!feeList?.content.find(f => f.feeId === feeId)?.officeId && !isAdminPage) {
          toast?.openToast(FeeToast, 4000);
        } else {
          setConfirmationOpen(true);
        }
        break;
      case "Copy Fee":
        const fee = feeList?.content.find(f => f.feeId === feeId);
        if (fee) copyFeeToOfficeFunc(fee);
        break;
      default:
        toast?.openToast();
        break;
    }
    setTooltipStates([...tooltipStates].fill(false));
  };

  const copyFeeToOfficeFunc = async (fee: Fee) => {
    if (fee?.feeId) {
      try {
        setIsLoading(true);
        const res: any = await dispatch(
          copyFeeToOffice({
            officeId,
            feeId: fee?.feeId,
          })
        );
        if (res?.payload && !isEmpty(res?.payload)) {
          handleTooltipClick("Edit Fee", res?.payload?.feeId);
          toast?.openToast("Fee copied successfully.");
          setHeaders(headersInitialState);
          setActiveIndex(0);
          updateTable(activeOwner, { sortedBy: sort.sortedBy, pageNumber: sort.pageNumber, rowsPerPage: sort.rowsPerPage }, searchTerm);
        }
        setIsLoading(false);
      } catch (_) {
        setIsLoading(false);
      }
    }
  };

  const onSelectFeeForCopy = (feeId: string) => {
    if (feeId) {
      const index = selectedFeeIdsForCopy.findIndex(item => item.feeId === feeId);
      if (index === -1) {
        setSelectedFeeIdsForCopy([...selectedFeeIdsForCopy, { feeId: feeId, completed: false }]);
      } else {
        setSelectedFeeIdsForCopy([...selectedFeeIdsForCopy.slice(0, index), ...selectedFeeIdsForCopy.slice(index + 1)]);
        setSelectAll(false);
      }
    }
  };

  const selectAllFeeIds = () => {
    const services = feeList?.content?.length ? feeList?.content : [];
    let newIds = [...selectedFeeIdsForCopy];
    services.map((item: Fee) => {
      if (item?.feeId) {
        const index = newIds.findIndex(itm => itm.feeId === item?.feeId);
        if (index === -1) {
          newIds = [...newIds, { feeId: item?.feeId, completed: false }];
        }
      }
      return true;
    });
    setSelectedFeeIdsForCopy(newIds);
  };

  const onSelectAll = (val: boolean) => {
    setSelectAll(val);
    if (val) {
      selectAllFeeIds();
    } else {
      setSelectedFeeIdsForCopy([]);
    }
  };

  const copyServiceFn = (feeId: string) => {
    return new Promise(async (resolve, reject) => {
      const res: any = await dispatch(
        copyFeeToOffice({
          officeId,
          feeId: feeId,
        })
      );
      if (res?.payload && !isEmpty(res?.payload)) {
        previousCompletedItems = [...previousCompletedItems, feeId];
        setCompletedCopiedFees(previousCompletedItems);
        resolve(true);
      } else {
        reject(false);
      }
    });
  };

  const copySelectedServicesToOffice = async () => {
    try {
      setIsLoading(true);
      previousCompletedItems = [...completedFees];
      await selectedFeeIdsForCopy
        .filter(item => !item.completed)
        .reduce((accumulatorPromise: any, item) => {
          return accumulatorPromise.then(() => {
            return copyServiceFn(item.feeId);
          });
        }, Promise.resolve());
      toast?.openToast("Fees Successfully Copied To Office", 2000, ToastVariants.Success);
      setSelectedFeeIdsForCopy([]);
      setCompletedCopiedFees([]);
      previousCompletedItems = [];
      setSelectAll(false);
      setIsLoading(false);
      changeServiceTab(0);
    } catch (err) {
      toast?.openToast("Fees Copied To Office Failed!", 2000, ToastVariants.Error);
      setIsLoading(false);
    }
  };

  const handleImportAll = async () => {
    if (currentOffice?.officeId) {
      const res: any = await dispatch(copyAllFeeToOffice({ officeId: currentOffice?.officeId }));
      if (res.payload && res.payload?.length) {
        setSelectAll(false);
        setSelectedFeeIdsForCopy([]);
        toast?.openToast("Fees Successfully Copied To Office", 2000, ToastVariants.Success);
        changeServiceTab(0);
      } else {
        toast?.errorToast("Importing all fee to office failed.");
      }
    }
  };

  const debounced = useDebouncedCallback(value => {
    if (value && value.trim().length >= 2) {
      updateTable(activeOwner, { ...sort, pageNumber: 0 }, value.trim());
    }
  }, 1000);

  const handleSearchInput = (text: string) => {
    const input = text;
    const oldSearchTerm = searchTerm;
    setSearchTerm(input);
    debounced(input);
    if (oldSearchTerm !== "" && input.length <= 1) {
      updateTable(activeOwner, sort);
    }
  };

  const handleToolTipOpen = (index: number) => {
    setTooltipStates([...tooltipStates.slice(0, index).fill(false), true, ...tooltipStates.slice(index + 1).fill(false)]);
  };

  const handleToolTipClose = () => {
    setTooltipStates([...tooltipStates].fill(false));
  };

  const officId =
    isAdminPage && currentUser?.offices?.length && currentUser?.offices[0]?.officeId ? currentUser?.offices[0]?.officeId : currentOffice?.officeId;

  const handleDeleteFee = () => {
    if (officId && selectedFee) {
      const feeToArchive = feeList?.content.find(f => f.feeId === selectedFee);
      if (feeToArchive) {
        dispatch(restoreOfficeFee({ officeId: officId, fee: feeToArchive, archived: true })).then(res => {
          if (res.payload !== null) {
            updateTable(activeOwner);
            toast?.openToast("Fee archived successfully", 2000, ToastVariants.Success);
          } else {
            toast?.openToast("Failed to archive fee", 2000, ToastVariants.Error);
          }
        });
      }
    }
    setConfirmationOpen(false);
    setSelectedFee(null);
  };

  const handleRowClick = (id: string) => {
    if (id) {
      setSelectedFee(id);
      if (!feeList?.content.find(f => f.feeId === id)?.officeId && !isAdminPage) {
        setViewFeeSidebarVisible(true);
      } else {
        if (currentUser?.permissions.includes(AuthPermission.Service)) {
          setEditFeeFormVisible(true);
        } else {
          setViewFeeSidebarVisible(true);
        }
      }
    }
  };

  const changeServiceTab = (index: number) => {
    setActiveIndex(index);
    if (index === 1) {
      setHeaders(headersDocTemplates);
    } else {
      setHeaders(headersInitialState);
    }
    let owner = ServiceOwner.Office;
    if (index === 1) {
      owner = ServiceOwner.System;
    }
    setActiveOwner(owner);
    // Only update table if not on Archived Fee tab
    if (index !== 2) {
      updateTable(owner, { ...sort, pageNumber: 0 }, searchTerm);
    }
  };

  useEffect(() => {
    if (selectAll && activeIndex === 1) {
      selectAllFeeIds();
    }
  }, [feeList?.content, activeIndex, selectAll]);

  useEffect(() => {
    setTooltipStates(new Array(feeList?.content ? feeList.content.length : 0).fill(false));
  }, [feeList]);

  useEffect(() => {
    if (!firstRender) {
      updateTable(activeOwner);
    }
  }, [officeId, firstRender]);

  useEffect(() => {
    if (currentOffice && currentOffice.officeId) setOfficeId(currentOffice?.officeId);
  }, [currentOffice]);

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      dispatch(resetFeeTableSort());
    }
  }, []);

  const feeTabOptions = ["Office Fees", "Docnote Templates", "Archived Fee"];
  const canEditFee = currentUser?.permissions?.includes(AuthPermission.Service);

  return (
    <main>
      {confirmationOpen ? (
        <ConfirmationDialog
          open={confirmationOpen}
          description={`This will permanently delete this fee, proceed?`}
          title={""}
          successButtonText={"Yes"}
          handleSuccess={handleDeleteFee}
          failureButtonText={"No"}
          handleFailure={() => setConfirmationOpen(false)}
        />
      ) : null}
      <div className="container mx-auto p-8">
        {!isAdminPage ? <OfficeSectionsHeader showMeta={true} /> : <BreadCrumbs items={[{ key: "#", title: "Fees" }]} />}
        <div className="block lg:flex justify-between items-center mb-6">
          <div className="mb-5 lg:mb-0">
            <h2 className="heading uppercase">Fees</h2>
          </div>
          <div className="block md:flex lg:justify-end flex-wrap w-full">
            {activeIndex !== 2 ? (
              <div className="flex items-center">
                <div className="relative w-full">
                  <TextInput
                    onChangeFunc={e => {
                      handleSearchInput(e.currentTarget.value);
                    }}
                    value={searchTerm}
                    placeholder="Search fees"
                    extraInputClass="w-full"
                    isSearchInput={true}
                  />
                </div>
              </div>
            ) : null}
            {activeIndex === 0 ? (
              <ProtectedComponent requiredPermission={[AuthPermission.Service]}>
                <div className="block md:flex md:justify-end items-center ml-0 md:ml-5">
                  <span className="md:mr-5 font-medium text-center hidden md:inline-block">or</span>
                  <Button text="Create Fee" onClick={() => feeSideBar(true)} className="whitespace-nowrap w-auto mt-3 md:mt-0" />
                </div>
              </ProtectedComponent>
            ) : null}
            {activeIndex === 1 ? (
              <div className="relative w-auto ml-5 gap-5 flex items-center">
                <Button
                  className="w-auto"
                  text="Copy Selected Fees To Office"
                  disabled={!selectedFeeIdsForCopy.length || isLoading || loading}
                  onClick={copySelectedServicesToOffice}
                />
                <Button className="w-auto" text="Copy All" disabled={loading} onClick={handleImportAll} />
              </div>
            ) : null}
          </div>
        </div>
        <p className="text-base font-medium my-5">
          Manage and customize your fees for common tasks. Use the Docnote template, based on best practices, to easily copy suggested fees to your
          office.
        </p>

        <div>
          {!isAdminPage ? (
            <div className="mb-5">
              <Tabs
                components={[]}
                options={feeTabOptions}
                activeTab={activeIndex}
                onClickTab={changeServiceTab}
                fromOffice={false}
                showBottomBorder={false}
              />
            </div>
          ) : null}
          {activeIndex === 2 ? (
            <ArchivedFees officeId={officeId} />
          ) : !loading ? (
            <div className="block">
              {feeList?.content && feeList?.content?.length > 0 ? (
                <>
                  <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
                    {headers.map((head, index: number) => (
                      <TableHeader
                        title={head.title}
                        index={index}
                        key={index}
                        sortIcon={sortIcons[index]}
                        isClickable={head.isClickable}
                        handleSort={onHeaderClick}
                        extraClassesHeader={extraClasses[index]}
                        isCheckbox={head?.isCheckbox}
                        isChecked={selectAll}
                        isCheckboxDisabled={isLoading}
                        selectAll={(val: boolean) => (!isLoading ? onSelectAll(val) : null)}
                      />
                    ))}
                  </div>

                  {feeList?.content?.length
                    ? feeList?.content.map((rowData: Fee, index: number) => {
                        let sortedData = null;
                        if (activeIndex === 1) {
                          const copiedItem = selectedFeeIdsForCopy.find(item => item.feeId === rowData?.feeId);
                          sortedData = {
                            selectAll: (
                              <div className="ml-0" onClick={e => e.stopPropagation()}>
                                {copiedItem?.feeId && completedFees.includes(copiedItem?.feeId) ? (
                                  <CheckIcon fontSize="small" className="green-color-icon" htmlColor="#22C55E" />
                                ) : copiedItem?.feeId && isLoading ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Checkbox
                                    label=""
                                    disabled={!canEditFee}
                                    checked={rowData?.feeId ? !!copiedItem : false}
                                    onChange={e => {
                                      onSelectFeeForCopy(rowData?.feeId ?? "");
                                    }}
                                  />
                                )}
                              </div>
                            ),
                            service: rowData.name,
                            description: <div className="flex justify-between items-center">{rowData.description ? rowData.description : ""}</div>,
                            price: "$" + formatNumToDec(rowData.price / 100, 2),
                          };
                        } else {
                          sortedData = {
                            service: rowData.name,
                            description: <div className="flex justify-between items-center">{rowData.description ? rowData.description : ""}</div>,
                            price: "$" + formatNumToDec(rowData.price / 100, 2),
                            status: !isAdminPage ? (
                              <div className="ml-3.5" onClick={e => e.stopPropagation()}>
                                <Checkbox
                                  label=""
                                  checked={rowData.enabled}
                                  onChange={e => {
                                    if (officeId && rowData.feeId) {
                                      dispatch(
                                        enableOfficeFee({
                                          fee: {
                                            ...rowData,
                                            enabled: !rowData.enabled,
                                          },
                                          officeId,
                                        })
                                      )
                                        .then(action => {
                                          if (action.payload !== null) {
                                            updateTable(activeOwner, sort, searchTerm);
                                          }
                                        })
                                        .catch(() => {});
                                    }
                                  }}
                                />
                              </div>
                            ) : rowData.enabled ? (
                              "Enabled"
                            ) : (
                              "Disabled"
                            ),
                          };
                        }
                        return (
                          <TableRow
                            data={Object.values(sortedData)}
                            key={index}
                            id={rowData.feeId}
                            extraClassesRow={extraClasses}
                            tooltipOptions={tooltipOptions}
                            handleTooltipClick={(option, id) => handleTooltipClick(option, id)}
                            handleCloseTooltip={handleToolTipClose}
                            handleOpenTooltip={() => handleToolTipOpen(index)}
                            tooltipVisible={tooltipStates[index]}
                            onRowClick={id => (id ? handleRowClick(id) : null)}
                            //disabledOptions={!rowData.officeId && !isAdminPage ? [tooltipOptions[1], tooltipOptions[2]] : []}
                          />
                        );
                      })
                    : null}

                  <Pagination
                    colSpan={10}
                    count={feeList?.content ? feeList.totalElements : 0}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                    page={sort.pageNumber}
                    rowsPerPage={sort.rowsPerPage}
                  />
                </>
              ) : (
                <DataNotFound image={NotFound} text="No fees found" />
              )}
            </div>
          ) : (
            <CircularProgress size={40} color={"info"} style={{ marginLeft: "50%", marginTop: "10%" }} />
          )}
          <UserSideBar
            visible={showAddFeeSideBar}
            LeftbuttonText="Cancel"
            RightbuttonText="Add"
            onCancelClick={() => feeSideBar(false)}
            onInviteClick={() => feeSideBar(false)}
            onClickOutside={() => (!isLoading ? feeSideBar(false) : null)}
            title="Add Fee"
          >
            <AddFee
              onCancelClick={val => {
                feeSideBar(false);
                if (val) {
                  updateTable(activeOwner, { sortedBy: sort.sortedBy, pageNumber: sort.pageNumber, rowsPerPage: sort.rowsPerPage }, searchTerm);
                }
              }}
              isAdminPage={isAdminPage}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
            />
          </UserSideBar>
          <UserSideBar
            visible={editFeeFormVisible && selectedFee !== null}
            LeftbuttonText="Cancel"
            RightbuttonText="Add"
            onCancelClick={() => setEditFeeFormVisible(false)}
            onInviteClick={() => setEditFeeFormVisible(false)}
            onClickOutside={() => (!isLoading ? setEditFeeFormVisible(false) : null)}
            title="Edit Fee"
          >
            <EditFee
              feeId={selectedFee || ""}
              disabled={!currentUser?.permissions.includes(AuthPermission.Service)}
              onCancelClick={val => {
                setEditFeeFormVisible(false);
                if (val) {
                  updateTable(activeOwner, { sortedBy: sort.sortedBy, pageNumber: sort.pageNumber, rowsPerPage: sort.rowsPerPage }, searchTerm);
                }
              }}
              isAdminPage={isAdminPage}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
            />
          </UserSideBar>
          <UserSideBar
            visible={viewFeeSidebarVisible && selectedFee !== null}
            onClickOutside={() => setViewFeeSidebarVisible(false)}
            title="View Fee"
          >
            <ViewFee
              feeId={selectedFee || ""}
              onCancelClick={() => setViewFeeSidebarVisible(false)}
              isLoading={isLoading}
              onCopyClick={(fee: Fee) => copyFeeToOfficeFunc(fee)}
            />
          </UserSideBar>
        </div>
      </div>
    </main>
  );
};

export default FeesScreen;
